import { action, computed, observable, makeAutoObservable } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { EventsCollectionQueryType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';
import {
    EventsCollectionQueryCompetitionWithEventsType,
    EventsCollectionQueryModel,
} from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import { DateTime } from 'src_common/utils/time/time';

export class OutrightBetState {
    @observable private activeCompetitionIdInner: number | null = null;
    @observable private activeMarketSlugInner: string | null = null;

    public constructor(
        private readonly appState: AppState,
        private readonly sport: string
    ) {
        makeAutoObservable(this);
    }

    private readonly getSportStartTime = (sport: string): string => {
        const startOfDay = DateTime.current().startOfDays();

        switch (sport) {
            case 'cricket':
                return startOfDay.subtractDays(6).toISOString();
            case 'snooker':
                return startOfDay.subtractDays(2).toISOString();
            case 'tennis':
                return startOfDay.subtractDays(2).toISOString();
            case 'golf':
                return startOfDay.subtractDays(5).toISOString();
            case 'horseracing':
            case 'greyhoundracing':
                return DateTime.current().subtractHours(6).isSameDay(DateTime.current())
                    ? startOfDay.toISOString()
                    : startOfDay.subtractHours(6).toISOString();
            default:
                return startOfDay.subtractHours(12).toISOString();
        }
    };

    @computed private get query(): EventsCollectionQueryType {
        return {
            state: 'open',
            display: false,
            sport: [this.sport],
            'startTime[from]': this.getSportStartTime(this.sport),
        };
    }

    @computed public get queryModel(): EventsCollectionQueryModel | null {
        return this.appState.websocketV2.getEventQuery(this.query);
    }

    @computed public get competitions(): Array<EventsCollectionQueryCompetitionWithEventsType> {
        return this.queryModel?.competitionsRaw ?? [];
    }

    @computed public get activeCompetitionId(): number | null {
        if (this.activeCompetitionIdInner === null) {
            return this.competitionFilter[0]?.id ?? null;
        }
        return this.activeCompetitionIdInner;
    }

    @computed public get activeMarketSlug(): string | null {
        if (this.activeMarketSlugInner === null) {
            return this.marketsFilter[0]?.slug ?? null;
        }
        return this.activeMarketSlugInner;
    }

    @computed public get markets(): Array<EventMarketItemType> {
        const markets = [];
        const competition = this.competitions.find((elem) => elem.id === this.activeCompetitionId) ?? null;

        if (competition === null) {
            return [];
        }

        for (const event of competition.events) {
            const marketsAllForEvent = this.appState.websocketV2.getEventMarketsAll(event.id);
            if (marketsAllForEvent !== null) {
                markets.push(...marketsAllForEvent);
            }
        }

        return markets;
    }

    @computed public get marketsFilterMap(): Map<string, { slug: string; name: string }> {
        const filtersMap: Map<string, { slug: string; name: string }> = new Map();

        for (const market of this.markets) {
            const slug = market.name.toLowerCase().replaceAll(' ', '-');
            filtersMap.set(slug, {
                slug: slug,
                name: market.name,
            });
        }

        return filtersMap;
    }

    @computed public get marketsFilter(): Array<{ slug: string; name: string }> {
        return Array.from(this.marketsFilterMap.values());
    }

    @computed public get competitionFilter(): Array<{ id: number; name: string }> {
        const competitionFilterMap: Map<number, { id: number; name: string }> = new Map();
        for (const competition of this.competitions) {
            const eventModel = competition.events.filter((event) => {
                const eventModel = this.appState.websocketV2.getEvent(event.id);
                if (eventModel !== null) {
                    return eventModel.markets.length > 0;
                }
                return false;
            });

            if (eventModel.length > 0) {
                competitionFilterMap.set(competition.id, { id: competition.id, name: competition.name });
            }
        }
        return Array.from(competitionFilterMap.values());
    }

    @computed public get selectedMarketName(): string {
        return this.activeMarketSlug === null ? '' : this.marketsFilterMap.get(this.activeMarketSlug)?.name ?? '';
    }

    @computed public get events(): Array<{
        id: number;
        name: string;
        markets: Array<EventMarketItemType>;
        timeSettingsStartTime: string;
        competition: EventsCollectionQueryCompetitionWithEventsType;
    }> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const eventsList: Array<any> = [];
        const competition = this.competitions.find((elem) => elem.id === this.activeCompetitionId) ?? null;

        if (competition === null) {
            return [];
        }

        for (const event of competition.events) {
            const eventModel = this.appState.websocketV2.getEvent(event.id);
            const marketsAllForEvent = this.appState.websocketV2.getEventMarketsAll(event.id) ?? [];
            const markets = marketsAllForEvent.filter((market) => market.name === this.selectedMarketName);

            if (eventModel !== null && markets.length > 0) {
                eventsList.push({
                    id: eventModel.id,
                    name: eventModel.name,
                    timeSettingsStartTime: eventModel.timeSettingsStartTime,
                    markets: markets,
                    competition: competition,
                });
            }
        }

        return eventsList;
    }

    @action public onOpenCompetition = (competitionId: number): void => {
        this.activeCompetitionIdInner = competitionId;
        this.activeMarketSlugInner = null;
    };

    @action public onFilterMarketSlug = (slug: string): void => {
        this.activeMarketSlugInner = slug;
    };
}
